"use client";

import { useEffect, useRef } from "react";
import gsap from "gsap";
import { tokenHsla } from "@/lib/theme";

interface Props {
  value: number | null;
  className?: string;
}

/**
 * Price readout that tweens between values and flashes green/red on direction.
 *
 * Deliberately not a generic count-up: it animates the NUMBER, not just the opacity, so the
 * eye tracks magnitude and direction rather than "something changed". Tabular figures (.tnum)
 * keep the width fixed so the layout can't jitter mid-tween.
 */
const AnimatedPrice = ({ value, className }: Props) => {
  const ref = useRef<HTMLSpanElement>(null);
  const prev = useRef<number | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el || value == null) return;

    const from = prev.current;
    prev.current = value;

    const render = (v: number) => {
      el.textContent = v.toLocaleString("en-NG", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    };

    // First paint, or reduced motion: just show the number.
    if (from == null || from === value) {
      render(value);
      return;
    }

    const mm = gsap.matchMedia();
    mm.add("(prefers-reduced-motion: reduce)", () => {
      render(value);
    });
    mm.add("(prefers-reduced-motion: no-preference)", () => {
      const obj = { v: from };
      gsap.to(obj, {
        v: value,
        duration: 0.5,
        ease: "power2.out",
        onUpdate: () => render(obj.v),
      });
      // Direction flash, same --buy/--sell tokens as the rest of the UI, so it follows the
      // user's colour choice (incl. the colourblind-safe preset). Resolved via tokenHsla
      // because GSAP cannot parse `hsl(var(--…))`: handing it one crashed production on
      // every ticker frame (2026-07-18). This tween was the primary trigger: it fires on
      // every mid-price change.
      gsap.fromTo(
        el,
        { color: tokenHsla(`--${value > from ? "buy" : "sell"}`) },
        { color: tokenHsla("--text-primary"), duration: 1.2, ease: "power2.out" }
      );
    });
    return () => mm.revert();
  }, [value]);

  return (
    <span ref={ref} className={`tnum ${className ?? ""}`}>
      {value == null ? "n/a" : value.toLocaleString("en-NG", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
    </span>
  );
};

export default AnimatedPrice;
